import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Tooltip,
  Alert,
  Divider,
  Grid,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Collapse,
  CircularProgress,
  Select,
  InputLabel,
  FormControl,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome,
  Shield,
  Refresh,
  Lightbulb,
} from '@mui/icons-material';
import { analyzeSpamScore } from './SpamDetector';
import { generateEmailWithAI, rewriteToAvoidSpam } from './AIEmailWriter';

const AIEmailWriterCard = ({ 
  subject,
  body,
  onUpdate, // Callback to update parent's subject and body
  mergeTags = [],
  buttonText = "Generate Email", // Customizable button text
  isMobile = false
}) => {
  // AI Dialog State
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiLength, setAiLength] = useState('medium');
  const [aiLoading, setAiLoading] = useState(false);

  // Spam Detection State
  const [spamAnalysis, setSpamAnalysis] = useState(null);
  const [showSpamDetails, setShowSpamDetails] = useState(false);
  const [rewriteLoading, setRewriteLoading] = useState(false);

  // Analyze spam score whenever subject or body changes
  useEffect(() => {
    if (subject || body) {
      const analysis = analyzeSpamScore(subject, body);
      setSpamAnalysis(analysis);
    }
  }, [subject, body]);

  // Generate email with AI
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      alert('Please describe what email you want to generate!');
      return;
    }

    setAiLoading(true);
    try {
      console.log('🤖 AIEmailWriterCard: Starting generation...');
      console.log('📝 Prompt:', aiPrompt);
      console.log('🎨 Tone:', aiTone, 'Length:', aiLength);
      console.log('🏷️ Merge tags count:', mergeTags.length);
      
      const result = await generateEmailWithAI(aiPrompt, {
        tone: aiTone,
        length: aiLength,
        industry: 'logistics',
        includeGreeting: true,
        includeClosing: true,
        avoidSpam: true,
        mergeTags: mergeTags,
      });
      
      console.log('📬 AI Result:', result);
      
      if (result.success) {
        console.log('✅ AI generation successful');
        
        // Extract subject from AI response
        const emailContent = result.email.trim();
        console.log('📄 Email content length:', emailContent.length);
        console.log('🔍 First 300 chars:', emailContent.substring(0, 300));
        
        let subject = '';
        let body = '';
        
        // Try to extract subject (multiple patterns)
        const subjectMatch = emailContent.match(/<p><strong>Subject:\s*(.+?)<\/strong><\/p>/i) ||
                            emailContent.match(/^Subject:\s*(.+?)$/im) ||
                            emailContent.match(/Subject:\s*(.+?)(?:\n|<)/i);
        
        if (subjectMatch) {
          subject = subjectMatch[1].replace(/<[^>]+>/g, '').trim();
          body = emailContent
            .replace(/<p><strong>Subject:\s*.+?<\/strong><\/p>/i, '')
            .replace(/^Subject:\s*.+?(\n|$)/im, '')
            .trim();
          body = body.replace(/^(<p>\s*<\/p>\s*)+/, '').trim();
          
          console.log('✅ Extracted Subject:', subject);
          console.log('✅ Extracted Body length:', body.length);
        } else {
          console.log('⚠️ No subject found, using full content as body');
          body = emailContent;
        }
        
        // Update parent component
        console.log('🔄 Calling onUpdate with:', { 
          subjectLength: subject.length, 
          bodyLength: body.length 
        });
        
        onUpdate({
          subject: subject,
          body: body
        });
        
        console.log('✅ onUpdate called successfully');
      } else {
        console.error('❌ AI generation failed:', result.error);
        alert(`AI generation failed: ${result.error}`);
      }

      setAiDialogOpen(false);
      setAiPrompt('');
    } catch (error) {
      console.error('❌ AI generation error:', error);
      alert('Failed to generate email with AI. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Remove spam words
  const handleRewriteSpamFree = async () => {
    // Ensure body is a string
    const bodyText = typeof body === 'string' ? body : String(body || '');
    
    if (!bodyText.trim()) {
      alert('Email body is empty!');
      return;
    }

    console.log('🛡️ Starting spam removal...');
    console.log('📝 Original body type:', typeof body);
    console.log('📝 Original body length:', bodyText.length);
    console.log('🔍 First 200 chars:', bodyText.substring(0, 200));

    setRewriteLoading(true);
    try {
      // Get spam words from analysis
      const spamWordsToRemove = spamAnalysis?.triggers || [];
      console.log('🚫 Spam words to remove:', spamWordsToRemove);
      
      const result = await rewriteToAvoidSpam(bodyText, spamWordsToRemove);
      
      console.log('✅ Spam removal result:', result);
      
      if (result.success) {
        const cleanBody = result.email || result.text || bodyText;
        console.log('✨ Clean body length:', cleanBody.length);
        console.log('✨ Clean body preview:', cleanBody.substring(0, 200));
        
        onUpdate({ subject, body: cleanBody });
        console.log('✅ Updated parent with clean body');
      } else {
        console.error('❌ Spam removal failed:', result.error);
        alert(`Failed to remove spam words: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Spam removal error:', error);
      alert('Failed to remove spam words. Please try again.');
    } finally {
      setRewriteLoading(false);
    }
  };

  // Quick template handler
  const handleUseTemplate = (templateType) => {
    const templates = {
      dispatching: 'Write a professional email to trucking companies introducing our premium truck dispatching services. Highlight 24/7 support, competitive rates, and proven track record.',
      mclease: 'Write an email to owner-operators about our MC lease program. Emphasize quick approval, affordable rates, and full compliance support.',
      loads: 'Write a professional email to carriers about available high-paying loads. Include pickup/delivery locations, rates, and quick payment terms.',
      partnership: 'Write an email proposing a strategic partnership with a logistics company. Focus on mutual benefits, growth opportunities, and industry expertise.'
    };
    setAiPrompt(templates[templateType] || '');
  };

  // Suggest replacements for spam words
  const suggestReplacements = (word) => {
    const replacements = {
      'free': ['complimentary', 'no-cost', 'included'],
      'guarantee': ['ensure', 'promise', 'committed to'],
      'money': ['revenue', 'earnings', 'income'],
      'cash': ['payment', 'compensation', 'funds'],
      'winner': ['recipient', 'selected', 'chosen'],
      'click here': ['visit our site', 'learn more', 'see details'],
      'buy now': ['order today', 'get started', 'purchase'],
      'urgent': ['important', 'timely', 'priority'],
      'act now': ['respond today', 'take action', 'get started'],
      'limited time': ['special offer', 'current promotion', 'this opportunity']
    };
    return replacements[word.toLowerCase()] || ['alternative', 'different word', 'synonym'];
  };

  return (
    <>
      {/* AI Email Writer Card */}
      <Card 
        variant="outlined" 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(138,43,226,0.08) 0%, rgba(75,0,130,0.08) 100%)',
          border: '2px solid rgba(138,43,226,0.3)'
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoAwesome sx={{ color: '#8a2be2' }} />
                <Typography variant="body2" fontWeight={700}>
                  AI Email Assistant & Spam Detection
                </Typography>
              </Stack>
              <Chip 
                label="Premium" 
                size="small" 
                sx={{ bgcolor: 'rgba(138,43,226,0.2)', color: '#8a2be2', fontWeight: 600 }}
              />
            </Stack>

            {/* AI Tools Buttons */}
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  startIcon={<AutoAwesome />}
                  onClick={() => setAiDialogOpen(true)}
                  sx={{ 
                    background: 'linear-gradient(135deg, #8a2be2 0%, #4b0082 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #9932cc 0%, #5a009f 100%)' }
                  }}
                >
                  Write Email with AI
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={rewriteLoading ? <CircularProgress size={16} /> : <Refresh />}
                  onClick={handleRewriteSpamFree}
                  disabled={!body || rewriteLoading}
                  color="secondary"
                >
                  Remove Spam Words
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Lightbulb />}
                  onClick={() => setShowSpamDetails(!showSpamDetails)}
                  color={spamAnalysis?.risk === 'high' ? 'error' : spamAnalysis?.risk === 'medium' ? 'warning' : 'success'}
                >
                  {showSpamDetails ? 'Hide' : 'Show'} Spam Details
                </Button>
              </Grid>
            </Grid>

            {/* Spam Score Display */}
            {spamAnalysis && (
              <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.5)', borderRadius: 1, border: '1px solid rgba(138,43,226,0.2)' }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Shield sx={{ fontSize: 32, color: spamAnalysis.risk === 'low' ? '#4caf50' : spamAnalysis.risk === 'medium' ? '#ff9800' : '#f44336' }} />
                  <Box flex={1}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={700}>
                        Spam Risk Score: {spamAnalysis.score.toFixed(0)}%
                      </Typography>
                      <Chip 
                        label={spamAnalysis.risk.toUpperCase()} 
                        size="small"
                        color={spamAnalysis.risk === 'low' ? 'success' : spamAnalysis.risk === 'medium' ? 'warning' : 'error'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>
                    <LinearProgress 
                      variant="determinate" 
                      value={spamAnalysis.score} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: spamAnalysis.risk === 'low' ? '#4caf50' : spamAnalysis.risk === 'medium' ? '#ff9800' : '#f44336'
                        }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" mt={0.5}>
                      {spamAnalysis.recommendation.message}
                    </Typography>
                  </Box>
                </Stack>

                {/* Spam Details - Collapsible */}
                <Collapse in={showSpamDetails}>
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="caption" fontWeight={700} gutterBottom display="block">
                      ⚠️ Issues Found ({spamAnalysis.warnings.length}):
                    </Typography>
                    <Stack spacing={0.5} maxHeight={200} overflow="auto" sx={{ pl: 1 }}>
                      {spamAnalysis.warnings.slice(0, 10).map((warning, idx) => (
                        <Typography key={idx} variant="caption" color="error" display="block">
                          • {warning}
                        </Typography>
                      ))}
                    </Stack>
                    
                    {spamAnalysis.highlightWords.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" fontWeight={700} gutterBottom display="block">
                          🔴 Spam Words Detected - Replace These:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {spamAnalysis.highlightWords.slice(0, 15).map((item, idx) => {
                            const suggestions = suggestReplacements(item.word);
                            return (
                              <Tooltip 
                                key={idx} 
                                title={`Replace with: ${suggestions.join(', ')}`}
                                arrow
                              >
                                <Chip
                                  label={`"${item.word}" (${item.count}x)`}
                                  size="small"
                                  color={item.risk === 'high' ? 'error' : item.risk === 'medium' ? 'warning' : 'default'}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* AI Email Writer Dialog */}
      <Dialog 
        open={aiDialogOpen} 
        onClose={() => setAiDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(138,43,226,0.15)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #8a2be2 0%, #4b0082 100%)', 
            color: '#fff',
            py: 2,
            px: 2.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome sx={{ fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
              AI Email Writer
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Stack spacing={2.5}>
            {/* Quick Templates */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 1.5 }}>
                🎯 Quick Templates
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="outlined" 
                    onClick={() => handleUseTemplate('dispatching')}
                    sx={{ 
                      borderColor: '#e0e0e0',
                      color: '#666',
                      py: 0.75,
                      fontSize: '0.75rem',
                      '&:hover': { 
                        borderColor: '#8a2be2', 
                        color: '#8a2be2',
                        bgcolor: 'rgba(138,43,226,0.03)'
                      }
                    }}
                  >
                    🚛 Dispatching
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="outlined" 
                    onClick={() => handleUseTemplate('mclease')}
                    sx={{ 
                      borderColor: '#e0e0e0',
                      color: '#666',
                      py: 0.75,
                      fontSize: '0.75rem',
                      '&:hover': { 
                        borderColor: '#8a2be2', 
                        color: '#8a2be2',
                        bgcolor: 'rgba(138,43,226,0.03)'
                      }
                    }}
                  >
                    📋 MC Lease
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="outlined" 
                    onClick={() => handleUseTemplate('loads')}
                    sx={{ 
                      borderColor: '#e0e0e0',
                      color: '#666',
                      py: 0.75,
                      fontSize: '0.75rem',
                      '&:hover': { 
                        borderColor: '#8a2be2', 
                        color: '#8a2be2',
                        bgcolor: 'rgba(138,43,226,0.03)'
                      }
                    }}
                  >
                    📦 Loads
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    fullWidth
                    size="small"
                    variant="outlined" 
                    onClick={() => handleUseTemplate('partnership')}
                    sx={{ 
                      borderColor: '#e0e0e0',
                      color: '#666',
                      py: 0.75,
                      fontSize: '0.75rem',
                      '&:hover': { 
                        borderColor: '#8a2be2', 
                        color: '#8a2be2',
                        bgcolor: 'rgba(138,43,226,0.03)'
                      }
                    }}
                  >
                    🤝 Partnership
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* AI Prompt Input */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
                ✨ Describe Your Email
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Example: Write a professional introduction to trucking companies about our dispatching services..."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem',
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                💡 AI will intelligently select and use merge tags from: {mergeTags.length > 0 ? mergeTags.slice(0, 4).map(t => t.tag).join(', ') : '{{name}}, {{company}}, {{mc_number}}, {{state}}'} based on your prompt
              </Typography>
            </Box>

            {/* Options */}
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tone</InputLabel>
                  <Select
                    value={aiTone}
                    onChange={(e) => setAiTone(e.target.value)}
                    label="Tone"
                  >
                    <MenuItem value="professional">💼 Professional</MenuItem>
                    <MenuItem value="friendly">😊 Friendly</MenuItem>
                    <MenuItem value="formal">🎩 Formal</MenuItem>
                    <MenuItem value="casual">👋 Casual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Length</InputLabel>
                  <Select
                    value={aiLength}
                    onChange={(e) => setAiLength(e.target.value)}
                    label="Length"
                  >
                    <MenuItem value="short">📝 Short</MenuItem>
                    <MenuItem value="medium">📄 Medium</MenuItem>
                    <MenuItem value="long">📋 Long</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Alert 
              severity="success" 
              icon={<Shield sx={{ fontSize: 18 }} />}
              sx={{ 
                py: 1,
                px: 1.5,
                '& .MuiAlert-message': { py: 0 }
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                ✓ Spam-Free Generation  ✓ Professional Language  ✓ High Deliverability
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2 }}>
          <Button 
            onClick={() => setAiDialogOpen(false)}
            sx={{ color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAIGenerate}
            variant="contained"
            disabled={!aiPrompt.trim() || aiLoading}
            startIcon={aiLoading ? <CircularProgress size={18} /> : <AutoAwesome />}
            sx={{ 
              background: 'linear-gradient(135deg, #8a2be2 0%, #4b0082 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #9932cc 0%, #5a009f 100%)' },
              minWidth: 140
            }}
          >
            {aiLoading ? 'Generating...' : buttonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIEmailWriterCard;
